// ─── RSVP Button Style Panel ──────────────────────────────────
// Mounted from ElementStylePanel when element.type === 'rsvp_button'.
// Exposes the same vocabulary the renderer reads from element.customStyle
// plus the translatable label (stored as element.content).
//
// Sections:
//   CONTENT  → label (text input), action (read-only)
//   BUTTON   → width, height, radius, fill, fill opacity, border, shadow
//   TEXT     → color, font, size, weight, letter spacing, uppercase toggle

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Type, Square, Palette, Link as LinkIcon, ExternalLink, Pencil } from 'lucide-react';

const SECTION_HEADER = 'text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5';
const FIELD_LABEL = 'text-[11px] text-gray-600 dark:text-gray-400 mb-1';
const INPUT = 'w-full text-[12px] px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-1 focus:ring-violet-400 focus:outline-none';

// Font vocabulary kept in sync with ElementStylePanel.FONT_OPTIONS so
// the RSVP button shares the same designer-facing type system as every
// other text element on the canvas. The empty-value entry ("Default")
// clears the override and lets the button inherit theme.bodyFont.
const FONT_OPTIONS = [
  { id: 'default', label: 'Default', value: '' },
  { id: 'serif', label: 'Serif', value: '"Playfair Display", Georgia, serif' },
  { id: 'sans', label: 'Sans', value: '"Inter", system-ui, sans-serif' },
  { id: 'elegant', label: 'Elegant', value: '"Cormorant Garamond", Georgia, serif' },
  { id: 'modern', label: 'Modern', value: '"DM Serif Display", Georgia, serif' },
  { id: 'handwritten', label: 'Hand', value: '"Caveat", cursive' },
  { id: 'typewriter', label: 'Mono', value: '"Special Elite", "Courier New", monospace' },
  { id: 'manrope', label: 'Clean', value: '"Manrope", system-ui, sans-serif' },
];

function Row({ children }) {
  return <div className="mb-2.5">{children}</div>;
}

function Slider({ label, value, min, max, step = 1, suffix, onChange, testid }) {
  const display = value === undefined || value === null || Number.isNaN(value) ? '' : `${value}${suffix || ''}`;
  return (
    <Row>
      <div className={FIELD_LABEL}>{label}</div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value ?? min}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-violet-600"
          data-testid={testid}
        />
        <span className="w-12 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums">
          {display}
        </span>
      </div>
    </Row>
  );
}

function ColorField({ label, value, onChange, testid }) {
  // Accept hex or transparent.
  const safe = value || '#000000';
  return (
    <Row>
      <div className={FIELD_LABEL}>{label}</div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={safe.startsWith('#') ? safe : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-7 h-7 rounded border border-gray-200 dark:border-gray-700 cursor-pointer"
          data-testid={`${testid}-picker`}
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${INPUT} flex-1`}
          placeholder="#000000 or transparent"
          data-testid={`${testid}-text`}
        />
      </div>
    </Row>
  );
}

export default function RsvpButtonStylePanel({ element, overrides, onUpdate, sectionId, eventId, slug, previewToken }) {
  const navigate = useNavigate();
  const cs = overrides?.customStyle || element.customStyle || {};
  const label = (overrides?.content ?? element.content ?? 'RSVP');

  const update = (patch) => onUpdate(sectionId, element.id, patch);
  const setStyle = (key, value) => update({ customStyle: { ...cs, [key]: value } });
  const setContent = (next) => update({ content: next });

  // The RSVP Button is just the entry point — the actual destination
  // (layout, copy, fields) lives in the RSVP Page Editor at event/
  // invitation level. These two actions are the bridge.
  const handleEditPage = () => {
    if (!eventId) return;
    navigate(`/events/${eventId}/rsvp-editor`);
  };
  const handlePreviewPage = () => {
    if (!slug) return;
    const qs = previewToken ? `?preview=${encodeURIComponent(previewToken)}` : '';
    window.open(`/invite/${slug}/rsvp${qs}`, '_blank', 'noopener');
  };

  // Element layout width/height live on element.layout, not customStyle.
  // Width is controlled via the existing resize handles + canvas tools.
  // Height we expose explicitly because pill buttons typically need a
  // specific minimum height for finger-friendly tap targets.
  const heightPx = Number.isFinite(cs.heightPx) ? cs.heightPx : 56;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-3 space-y-3" data-testid="rsvp-button-style-panel">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-violet-600">
        RSVP Button
      </h3>

      {/* ─── CONTENT ─────────────────────────────────────────── */}
      <div>
        <div className={SECTION_HEADER}><Type className="w-3 h-3" /> Content</div>
        <Row>
          <div className={FIELD_LABEL}>Label</div>
          <input
            type="text"
            value={label}
            onChange={(e) => setContent(e.target.value)}
            className={INPUT}
            placeholder="RSVP"
            data-testid="rsvp-btn-label-input"
          />
        </Row>
        <Row>
          <div className={FIELD_LABEL}>Action</div>
          <div className="text-[12px] px-2 py-1.5 rounded bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 flex items-center gap-1.5" data-testid="rsvp-btn-action-readonly">
            <LinkIcon className="w-3 h-3" /> Open RSVP page
          </div>
        </Row>
        {/* Page-level actions — the dedicated RSVP page (layout, copy,
            form fields) lives at event level, not on the button. */}
        <Row>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={handleEditPage}
              disabled={!eventId}
              className="inline-flex items-center justify-center gap-1.5 text-[11px] font-medium px-2 py-1.5 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="rsvp-btn-edit-page"
              title={eventId ? 'Open the RSVP Page Editor' : 'Event id missing'}
            >
              <Pencil className="w-3 h-3" />
              Edit RSVP page
            </button>
            <button
              type="button"
              onClick={handlePreviewPage}
              disabled={!slug}
              className="inline-flex items-center justify-center gap-1.5 text-[11px] font-medium px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="rsvp-btn-preview-page"
              title={slug ? 'Open the guest RSVP page in a new tab' : 'Slug not available yet'}
            >
              <ExternalLink className="w-3 h-3" />
              Preview
            </button>
          </div>
        </Row>
      </div>

      {/* ─── BUTTON SHAPE ────────────────────────────────────── */}
      <div>
        <div className={SECTION_HEADER}><Square className="w-3 h-3" /> Button</div>
        <Slider
          label="Height"
          value={heightPx}
          min={32}
          max={120}
          suffix="px"
          onChange={(v) => setStyle('heightPx', v)}
          testid="rsvp-btn-height"
        />
        <Slider
          label="Corner radius"
          value={Number.isFinite(cs.borderRadius) ? cs.borderRadius : 999}
          min={0}
          max={999}
          suffix="px"
          onChange={(v) => setStyle('borderRadius', v)}
          testid="rsvp-btn-radius"
        />
        <Slider
          label="Padding (horizontal)"
          value={Number.isFinite(cs.paddingX) ? cs.paddingX : 28}
          min={4}
          max={64}
          suffix="px"
          onChange={(v) => setStyle('paddingX', v)}
          testid="rsvp-btn-padding-x"
        />
        <ColorField
          label="Fill color"
          value={cs.bgColor || 'transparent'}
          onChange={(v) => setStyle('bgColor', v)}
          testid="rsvp-btn-bg"
        />
        <Slider
          label="Fill opacity"
          value={Math.round(((cs.bgOpacity !== undefined ? cs.bgOpacity : 100)))}
          min={0}
          max={100}
          suffix="%"
          onChange={(v) => setStyle('bgOpacity', v)}
          testid="rsvp-btn-bg-opacity"
        />
        <ColorField
          label="Border color"
          value={cs.borderColor || '#1a1a1a'}
          onChange={(v) => setStyle('borderColor', v)}
          testid="rsvp-btn-border-color"
        />
        <Slider
          label="Border width"
          value={Number.isFinite(cs.borderWidth) ? cs.borderWidth : 1}
          min={0}
          max={6}
          suffix="px"
          onChange={(v) => setStyle('borderWidth', v)}
          testid="rsvp-btn-border-width"
        />
        <Slider
          label="Shadow"
          value={Math.max(0, Math.min(100, Number(cs.shadowStrength) || 0))}
          min={0}
          max={100}
          onChange={(v) => setStyle('shadowStrength', v)}
          testid="rsvp-btn-shadow"
        />
      </div>

      {/* ─── TEXT ────────────────────────────────────────────── */}
      <div>
        <div className={SECTION_HEADER}><Palette className="w-3 h-3" /> Text</div>
        {/* Font family — mirrors ElementStylePanel's font grid so the
            RSVP button speaks the same type language as text blocks.
            Each swatch renders the actual button label in that face so
            designers can compare without clicking. */}
        <Row>
          <div className={FIELD_LABEL}>Font</div>
          <div className="grid grid-cols-2 gap-1.5" data-testid="rsvp-btn-font-grid">
            {FONT_OPTIONS.map((f) => {
              const active = (cs.fontFamily || '') === f.value;
              const previewText = (label || 'RSVP').trim() || 'RSVP';
              const previewTransform = cs.textTransform || 'uppercase';
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setStyle('fontFamily', f.value)}
                  title={f.label}
                  className={`group flex flex-col items-center justify-center px-2 py-1.5 rounded transition-all overflow-hidden ${
                    active
                      ? 'bg-violet-100 dark:bg-violet-900/30 ring-1 ring-violet-400'
                      : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 ring-1 ring-transparent'
                  }`}
                  data-testid={`rsvp-btn-font-${f.id}`}
                >
                  <span
                    className={`block w-full truncate text-center text-[13px] leading-tight ${
                      active ? 'text-violet-800 dark:text-violet-200' : 'text-gray-800 dark:text-gray-200'
                    }`}
                    style={{
                      fontFamily: f.value || undefined,
                      textTransform: previewTransform,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {previewText}
                  </span>
                  <span
                    className={`block text-[9px] mt-0.5 uppercase tracking-wider ${
                      active ? 'text-violet-500 dark:text-violet-400' : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {f.label}
                  </span>
                </button>
              );
            })}
          </div>
        </Row>
        <ColorField
          label="Text color"
          value={cs.color || cs.textColor || '#1a1a1a'}
          onChange={(v) => setStyle('color', v)}
          testid="rsvp-btn-color"
        />
        <Slider
          label="Font size"
          value={parseInt(cs.fontSize) || 14}
          min={9}
          max={48}
          suffix="px"
          onChange={(v) => setStyle('fontSize', `${v}px`)}
          testid="rsvp-btn-font-size"
        />
        <Row>
          <div className={FIELD_LABEL}>Font weight</div>
          <select
            value={cs.fontWeight || 500}
            onChange={(e) => setStyle('fontWeight', Number(e.target.value))}
            className={INPUT}
            data-testid="rsvp-btn-font-weight"
          >
            <option value={300}>Light (300)</option>
            <option value={400}>Regular (400)</option>
            <option value={500}>Medium (500)</option>
            <option value={600}>Semibold (600)</option>
            <option value={700}>Bold (700)</option>
          </select>
        </Row>
        <Slider
          label="Letter spacing"
          value={Number.isFinite(cs.letterSpacing) ? cs.letterSpacing : 1.6}
          min={-1}
          max={10}
          step={0.1}
          suffix="px"
          onChange={(v) => setStyle('letterSpacing', v)}
          testid="rsvp-btn-letter-spacing"
        />
        <Row>
          <label className="flex items-center gap-2 text-[12px] text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={(cs.textTransform || 'uppercase') === 'uppercase'}
              onChange={(e) => setStyle('textTransform', e.target.checked ? 'uppercase' : 'none')}
              className="accent-violet-600"
              data-testid="rsvp-btn-uppercase"
            />
            Uppercase
          </label>
        </Row>
      </div>
    </div>
  );
}
